import { isOnBoard, neighbors, pointKey, pointToLabel } from "./coordinates";
import { BLACK, EMPTY, WHITE, type Board, type Color, type Point, type Stone } from "./types";

export interface Group {
  color: Stone;
  stones: number[];
  liberties: Set<number>;
}

export interface MoveApplication {
  ok: boolean;
  board: Board;
  captures: number;
  error?: string;
}

export function createBoard(size: number): Board {
  return Array.from({ length: size * size }, () => EMPTY);
}

export function indexOf(point: Point, size: number): number {
  return point.y * size + point.x;
}

export function pointFromIndex(index: number, size: number): Point {
  return { x: index % size, y: Math.floor(index / size) };
}

export function colorToStone(color: Color): Stone {
  return color === "black" ? BLACK : WHITE;
}

export function stoneToColor(stone: Stone): Color | null {
  if (stone === BLACK) return "black";
  if (stone === WHITE) return "white";
  return null;
}

export function opponent(color: Color): Color {
  return color === "black" ? "white" : "black";
}

export function opponentStone(stone: Stone): Stone {
  return stone === BLACK ? WHITE : BLACK;
}

export function countStones(board: Board): number {
  return board.reduce<number>((total, stone) => total + (stone === EMPTY ? 0 : 1), 0);
}

export function getGroup(board: Board, size: number, start: Point | number): Group {
  const startIndex = typeof start === "number" ? start : indexOf(start, size);
  const color = board[startIndex];
  const visited = new Set<number>();
  const liberties = new Set<number>();
  const stones: number[] = [];
  const queue = [startIndex];

  while (queue.length > 0) {
    const current = queue.pop()!;
    if (visited.has(current)) continue;
    visited.add(current);
    stones.push(current);

    for (const neighbor of neighbors(pointFromIndex(current, size), size)) {
      const neighborIndex = indexOf(neighbor, size);
      if (board[neighborIndex] === EMPTY) {
        liberties.add(neighborIndex);
      } else if (board[neighborIndex] === color && !visited.has(neighborIndex)) {
        queue.push(neighborIndex);
      }
    }
  }

  return { color, stones, liberties };
}

export function applyMove(
  board: Board,
  size: number,
  point: Point,
  color: Color,
  allowSuicide = false,
  // Position immediately before the opponent's last move. When supplied, a move that recreates it is
  // rejected as a ko (simple ko / positional repetition), which is what stops the endless capture-recapture loop.
  previousBoard?: Board
): MoveApplication {
  if (!isOnBoard(point, size)) {
    return { ok: false, board, captures: 0, error: "That point is outside the board." };
  }

  const targetIndex = indexOf(point, size);
  if (board[targetIndex] !== EMPTY) {
    return { ok: false, board, captures: 0, error: `${pointToLabel(point, size)} is occupied.` };
  }

  const stone = colorToStone(color);
  const enemy = opponentStone(stone);
  const next = board.slice();
  next[targetIndex] = stone;

  let captures = 0;
  const checkedEnemyGroups = new Set<number>();

  for (const neighbor of neighbors(point, size)) {
    const neighborIndex = indexOf(neighbor, size);
    if (next[neighborIndex] !== enemy || checkedEnemyGroups.has(neighborIndex)) {
      continue;
    }

    const group = getGroup(next, size, neighborIndex);
    for (const stoneIndex of group.stones) {
      checkedEnemyGroups.add(stoneIndex);
    }

    if (group.liberties.size === 0) {
      captures += group.stones.length;
      for (const stoneIndex of group.stones) {
        next[stoneIndex] = EMPTY;
      }
    }
  }

  const ownGroup = getGroup(next, size, targetIndex);
  if (!allowSuicide && ownGroup.liberties.size === 0) {
    return { ok: false, board, captures: 0, error: "Suicide is not legal in play mode." };
  }

  if (previousBoard && boardSignature(next, size) === boardSignature(previousBoard, size)) {
    return { ok: false, board, captures: 0, error: "Ko: you cannot immediately recapture." };
  }

  return { ok: true, board: next, captures, error: undefined };
}

export function setStone(board: Board, size: number, point: Point, stone: Stone): Board {
  if (!isOnBoard(point, size)) {
    return board;
  }
  const next = board.slice();
  next[indexOf(point, size)] = stone;
  return next;
}

export function legalMoves(board: Board, size: number, color: Color): Point[] {
  const moves: Point[] = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const point = { x, y };
      if (applyMove(board, size, point, color).ok) {
        moves.push(point);
      }
    }
  }
  return moves;
}

export function adjacentGroups(board: Board, size: number, point: Point, stone: Stone): Group[] {
  const seen = new Set<string>();
  const groups: Group[] = [];
  for (const neighbor of neighbors(point, size)) {
    const neighborIndex = indexOf(neighbor, size);
    if (board[neighborIndex] !== stone) {
      continue;
    }
    const group = getGroup(board, size, neighborIndex);
    const key = group.stones.map(String).sort().join(":");
    if (!seen.has(key)) {
      seen.add(key);
      groups.push(group);
    }
  }
  return groups;
}

export function hasPoint(set: Set<number>, point: Point, size: number): boolean {
  return set.has(indexOf(point, size));
}

export function boardSignature(board: Board, size: number): string {
  const rows: string[] = [];
  for (let y = 0; y < size; y += 1) {
    rows.push(board.slice(y * size, (y + 1) * size).join(""));
  }
  return rows.join("/");
}

export function pointDebug(point: Point, size: number): string {
  return `${pointToLabel(point, size)} (${pointKey(point)})`;
}
